import actions from '@wc-stripe/actions';

/**
 * Express button skeleton loader.
 *
 * Each context (checkout banner, product page, ...) ships its express button row
 * visible with placeholder shimmer blocks already rendered server-side (the
 * `wc-stripe-express-loading` class on the container + a `.wc-stripe-express-skeleton` div inside each
 * per-gateway slot) so there's no first-paint delay. This module then swaps each
 * placeholder for the real button as its Apple Pay / Google Pay / Link gateway
 * resolves its availability, or collapses the row if none of them are available.
 *
 * It lives in its own bundle so PHP can decide whether to enqueue it per context
 * (e.g. skipped where a checkout plugin renders its own express area / loading UI).
 * It only listens on the shared actions bus and touches the DOM - no gateway or
 * controller code depends on it.
 *
 * @since 4.0.13
 */

const LOADING_CLASS = 'wc-stripe-express-loading';
const SKELETON_SELECTOR = '.wc-stripe-express-skeleton';

/**
 * Per-context markup: the container that holds the express buttons, and how to
 * find the per-gateway slot within it. Keyed by the controller's Context page.
 */
const CONTEXTS = {
	express_checkout: {
		container: '.wc-stripe-banner-checkout',
		slot: id => `.banner_payment_method_${id}`
	},
	product: {
		container: '.wc-stripe-product-checkout-container',
		slot: id => `.payment_method_${id}`
	},
	cart: {
		container: '.wc-stripe-cart-checkout-container',
		slot: id => `.payment_method_${id}`
	}
};

class SkeletonLoader {

	constructor(page, config) {
		this._page = page;
		this._config = config;
		this._resolved = new Set();
		this._total = 0;
		this._settled = false;

		this._scan();

		// Available: wait for the element's real `ready` (fired once the button is
		// actually rendered) so the shimmer doesn't blink out before the button paints.
		// Unavailable: the controller has already hidden the slot.
		//
		// There's deliberately no time-based fallback: connection speed is unknown
		// (the Stripe SDK load alone can exceed any fixed timeout on slow mobile), and
		// a stale timer firing mid-load causes the very shift this avoids. A gateway
		// that never renders (e.g. element load error) leaves its own slot shimmering;
		// the sibling slots that did resolve are unaffected.
		actions.addAction('express_element_ready', this._onReady.bind(this));
		actions.addAction('payment_unavailable', this._onUnavailable.bind(this));

		// A container can get replaced wholesale after the initial render - the
		// cart page does this itself (fresh skeleton markup + the loading class,
		// re-rendered server-side) on shipping/quantity/coupon changes via
		// `cart_dom_updated`, and a merchant customization could plausibly do the
		// same on the checkout page via `updated_checkout` (e.g. re-rendering the
		// banner into a different spot). Re-scan on both so that render is tracked
		// as its own cycle, instead of its ids being silently swallowed by the ones
		// this instance already resolved on the previous render (which would leave
		// stale skeleton markup and the loading class behind). A no-op wherever
		// nothing actually replaced the container - including the default checkout
		// flow, where ExpressCheckoutController re-initializes on every
		// `updated_checkout` without touching the DOM, so there's no fresh skeleton
		// markup for `_scan()` to find.
		//
		// Priority 5: Cart.js and ExpressCheckoutController each listen on these
		// same actions at the default priority (10) and, from those same handlers,
		// drive the express gateways to remount. We need `_scan()` to have already
		// reset `_resolved` before that remount cascade starts, or its
		// `express_element_ready`/`payment_unavailable` calls land on the stale set
		// and get swallowed exactly like before this fix.
		actions.addAction('cart_dom_updated', this._scan.bind(this), 5);
		actions.addAction('updated_checkout', this._scan.bind(this), 5);
	}

	_getContainer() {
		return document.querySelector(this._config.container);
	}

	/**
	 * (Re)computes the set of skeletons to track against the container currently
	 * in the DOM. Safe to call more than once - e.g. after the cart fragment
	 * that holds this container gets replaced.
	 */
	_scan() {
		const container = this._getContainer();
		if (!container) {
			return;
		}
		const skeletons = container.querySelectorAll(SKELETON_SELECTOR);
		if (!skeletons.length) {
			return;
		}

		this._resolved = new Set();
		this._total = skeletons.length;
		this._settled = false;
	}

	/**
	 * @param {Object} availablePaymentMethods Ignored - passed by the action.
	 * @param {Object} gateway                 AbstractExpressGateway instance.
	 */
	_onReady(availablePaymentMethods, gateway) {
		this._resolve(gateway && gateway.context, gateway && gateway.id);
	}

	/**
	 * @param {Object} controller BaseController instance passed by the action.
	 */
	_onUnavailable(controller) {
		this._resolve(controller && controller.context, controller && controller.gateway && controller.gateway.id);
	}

	_resolve(context, id) {
		if (this._settled || !context || context.getPage() !== this._page || !id || this._resolved.has(id)) {
			return;
		}
		this._resolved.add(id);

		const container = this._getContainer();
		if (container) {
			this._removeSkeleton(container.querySelector(this._config.slot(id)));
		}

		if (this._resolved.size >= this._total) {
			this._settle();
		}
	}

	_removeSkeleton(slot) {
		if (!slot) {
			return;
		}
		const skeleton = slot.querySelector(SKELETON_SELECTOR);
		if (skeleton) {
			skeleton.remove();
		}
	}

	/**
	 * Every slot has resolved - drop the loading state. If a gateway became
	 * available its controller has already added `.active`, which keeps the row
	 * visible; if none did, the base stylesheet hides it again.
	 */
	_settle() {
		if (this._settled) {
			return;
		}
		this._settled = true;

		const container = this._getContainer();
		if (container) {
			container.classList.remove(LOADING_CLASS);
		}
	}

}

const init = () => {
	Object.keys(CONTEXTS).forEach(page => {
		const config = CONTEXTS[page];
		const container = document.querySelector(config.container);
		if (container && container.querySelector(SKELETON_SELECTOR)) {
			new SkeletonLoader(page, config);
		}
	});
};

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', init);
} else {
	init();
}